import { z } from "zod";
import { patientApiRequest, ApiAuth } from "../../lib/api";

// The patient's own messaging state. The patient is NEVER sent: the server
// reads it from the verified token (/patient-app/messaging/*).

const UnreadRowSchema = z.object({
  conversationId: z.string(),
  unreadCount: z.number(),
});

export const UnreadSummarySchema = z.object({
  totalUnread: z.number().optional().default(0),
  conversationsWithUnread: z.number().optional().default(0),
  byConversation: z.array(UnreadRowSchema).optional().default([]),
});
export type UnreadSummary = z.infer<typeof UnreadSummarySchema>;

export const MarkReadResultSchema = z.object({
  conversationId: z.string(),
  lastReadAt: z.number(),
});
export type MarkReadResult = z.infer<typeof MarkReadResultSchema>;

const KIND_BY_USER_TYPE: Record<string, MemberKind> = {
  doctor: "clinician",
  admin: "admin",
  patient: "patient",
};

export const MemberKindSchema = z.enum(["clinician", "admin", "patient"]);
export type MemberKind = z.infer<typeof MemberKindSchema>;

export type MemberRef = { kind: MemberKind; id: string };

// The one userType -> kind map (server messagingModel.js KIND_BY_USER_TYPE).
// Throws on anything unmapped: a wrong kind targets the wrong person.
export function memberRefOf({ id, userType }: { id: unknown; userType: unknown }): MemberRef {
  const kind =
    typeof userType === "string" && Object.prototype.hasOwnProperty.call(KIND_BY_USER_TYPE, userType)
      ? KIND_BY_USER_TYPE[userType]
      : undefined;
  if (!kind) throw new Error(`Unknown userType for a member reference: ${String(userType)}`);
  if (typeof id !== "string" || id.length === 0) throw new Error("A member reference needs a non-empty id");
  return { kind, id };
}

export const MemberViewSchema = z.object({
  kind: MemberKindSchema,
  id: z.string(),
  role: z.enum(["owner", "admin", "member"]),
  isAdmin: z.boolean(),
  joinedAt: z.number().nullable().optional(),
  historyVisibleFrom: z.number().nullable(),
  name: z.string().nullable(),
  photo: z.string().nullable(),
});
export type MemberView = z.infer<typeof MemberViewSchema>;

export const PatientConversationViewSchema = z.object({
  conversationId: z.string(),
  kind: z.enum(["direct", "group"]),
  title: z.string().nullable(),
  status: z.string(),
  lastMessageAt: z.number().nullable(),
  lastMessage: z.unknown().nullable(),
  unreadCount: z.number(),
  historyClearedAt: z.number().nullable(),
  members: z.array(MemberViewSchema),
});
export type PatientConversationView = z.infer<typeof PatientConversationViewSchema>;

export const MessageViewSchema = z.object({
  messageId: z.string(),
  senderId: z.string(),
  senderKind: z.string().nullable(),
  userType: z.string().nullable(),
  messageType: z.string(),
  message: z.string(),
  attachment: z.unknown().nullable(),
  attachments: z.array(z.unknown()),
  timestamp: z.string(),
  createdAtMs: z.number().nullable(),
  isDeleted: z.boolean(),
  deletedAt: z.number().nullable(),
  // The id the sender's screen gave the message: how that screen knows its own (ADR 0018 §5).
  clientMessageId: z.string().nullable().optional(),
  callId: z.string().nullable().optional(),
});
export type MessageView = z.infer<typeof MessageViewSchema>;

/** The server's answer to a send: the message as stored, with the server's stamp. */
export const SentMessageSchema = z.object({
  conversationId: z.string(),
  redirectedFrom: z.string().nullable().optional(),
  message: MessageViewSchema,
});
export type SentMessage = z.infer<typeof SentMessageSchema>;

/** One message to send: its own id, minted once by this screen, and its content. */
export type MessageToSend = {
  conversationId: string;
  clientMessageId: string;
  messageType: "text" | "file";
  message: string;
  attachment?: unknown;
};

// A send with no answer by then is retried with the same id (outbox.js).
const SEND_TIMEOUT_MS = 20000;

export const ThreadPageSchema = z.object({
  conversationId: z.string(),
  messages: z.array(MessageViewSchema),
  hasMore: z.boolean(),
  nextBefore: z.string().nullable(),
  lastReadAt: z.number().nullable(),
});
export type ThreadPage = z.infer<typeof ThreadPageSchema>;

export const ConversationsResultSchema = z.object({
  conversations: z.array(PatientConversationViewSchema),
});
export type ConversationsResult = z.infer<typeof ConversationsResultSchema>;

export const DirectResultSchema = z.object({
  created: z.boolean(),
  conversation: PatientConversationViewSchema,
});
export type DirectResult = z.infer<typeof DirectResultSchema>;

export const LeaveResultSchema = z.object({
  conversationId: z.string(),
  status: z.literal("left"),
});
export type LeaveResult = z.infer<typeof LeaveResultSchema>;

export const DeleteMessageResultSchema = z.object({
  conversationId: z.string(),
  messageId: z.string(),
  deletedAt: z.number().nullable(),
});
export type DeleteMessageResult = z.infer<typeof DeleteMessageResultSchema>;

/** One channel of the patient's message alerts: whether it goes out today, and whether it can at all. */
const AlertChannelSchema = z.object({
  on: z.boolean(),
  available: z.boolean(),
});

/**
 * "A secure message is waiting", by email and by text (blhWebServer ADR 0018 §7).
 * `offered`: the clinic sends these alerts (the platform switched it on); the
 * choice is shown only then.
 */
const AlertsAnswerSchema = z.object({
  offered: z.boolean(),
  alerts: z.object({ email: AlertChannelSchema, sms: AlertChannelSchema }),
});
export type MessageAlerts = { offered: boolean } & z.infer<typeof AlertsAnswerSchema>["alerts"];
export type MessageAlertChanges = { email?: boolean; sms?: boolean };

function messageAlertsOf(data: unknown): MessageAlerts {
  const { offered, alerts } = AlertsAnswerSchema.parse(data);
  return { offered, ...alerts };
}

const CONVERSATIONS_URL = "/patient-app/messaging/conversations";
const ALERTS_URL = "/patient-app/messaging/alert-preferences";

export const patientMessagingApi = {
  async unreadSummary(auth: ApiAuth): Promise<UnreadSummary> {
    const data = await patientApiRequest(auth, {
      method: "GET",
      url: "/patient-app/messaging/unread-summary",
    });
    return UnreadSummarySchema.parse(data);
  },

  /**
   * Read up to what the screen showed (ADR 0018 §5): `upTo` is the `createdAtMs`
   * of the latest message shown, so one that arrived after stays unread.
   */
  async markRead(auth: ApiAuth, conversationId: string, upTo: number | null = null): Promise<MarkReadResult> {
    const data = await patientApiRequest(auth, {
      method: "POST",
      url: `/patient-app/messaging/conversations/${encodeURIComponent(conversationId)}/read`,
      ...(Number.isSafeInteger(upTo) ? { data: { upTo } } : {}),
    });
    return MarkReadResultSchema.parse(data);
  },

  /**
   * One message, sent (ADR 0018 §1, §5): stored by the server, then answered
   * with the stored message. Its id is the request's Idempotency-Key, so the
   * same send again is answered with the message already stored. No answer
   * throws an ApiError with status 0; a refusal, the server's sentence and code.
   */
  async sendMessage(auth: ApiAuth, { conversationId, clientMessageId, messageType, message, attachment }: MessageToSend): Promise<SentMessage> {
    const data = await patientApiRequest(auth, {
      method: "POST",
      url: `${CONVERSATIONS_URL}/${encodeURIComponent(conversationId)}/messages`,
      headers: { "Idempotency-Key": clientMessageId },
      data: { messageType, message, ...(attachment ? { attachment } : {}) },
      timeout: SEND_TIMEOUT_MS,
    });
    return SentMessageSchema.parse(data);
  },

  async listConversations(auth: ApiAuth): Promise<PatientConversationView[]> {
    const data = await patientApiRequest(auth, {
      method: "GET",
      url: CONVERSATIONS_URL,
    });
    return ConversationsResultSchema.parse(data).conversations;
  },

  async listMessages(
    auth: ApiAuth,
    conversationId: string,
    { limit, before }: { limit?: number; before?: string } = {}
  ): Promise<ThreadPage> {
    const params: { limit?: number; before?: string } = {};
    if (limit !== undefined) params.limit = limit;
    if (before !== undefined) params.before = before;
    const data = await patientApiRequest(auth, {
      method: "GET",
      url: `${CONVERSATIONS_URL}/${encodeURIComponent(conversationId)}/messages`,
      params,
    });
    return ThreadPageSchema.parse(data);
  },

  async startDirect(auth: ApiAuth, other: MemberRef): Promise<DirectResult> {
    const data = await patientApiRequest(auth, {
      method: "POST",
      url: `${CONVERSATIONS_URL}/direct`,
      data: { other: { kind: other.kind, id: other.id } },
    });
    return DirectResultSchema.parse(data);
  },

  async leaveConversation(auth: ApiAuth, conversationId: string): Promise<LeaveResult> {
    const data = await patientApiRequest(auth, {
      method: "POST",
      url: `${CONVERSATIONS_URL}/${encodeURIComponent(conversationId)}/leave`,
    });
    return LeaveResultSchema.parse(data);
  },

  async deleteMessage(auth: ApiAuth, conversationId: string, messageId: string): Promise<DeleteMessageResult> {
    const data = await patientApiRequest(auth, {
      method: "DELETE",
      url: `${CONVERSATIONS_URL}/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}`,
    });
    return DeleteMessageResultSchema.parse(data);
  },

  /** The patient's message alerts: per channel, on today and available at all. */
  async messageAlerts(auth: ApiAuth): Promise<MessageAlerts> {
    const data = await patientApiRequest(auth, { method: "GET", url: ALERTS_URL });
    return messageAlertsOf(data);
  },

  /** Turns a channel on or off, and answers the alerts as stored. A refusal throws the server's sentence. */
  async setMessageAlerts(auth: ApiAuth, changes: MessageAlertChanges): Promise<MessageAlerts> {
    const data = await patientApiRequest(auth, { method: "PUT", url: ALERTS_URL, data: changes });
    return messageAlertsOf(data);
  },
};
